/** Fase 4 — visão individual do atleta para a equipe: alertas, evolução, metas, provas, bem-estar e recordes. */
import { api } from "../../api/client";
import type { AthleteDetail, Records } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import { BodyStaff } from "../../components/body";
import { StrengthOverviewSection } from "../../components/StrengthOverview";
import { AlertsList, EvolutionCharts, GoalsPanel, PeriodFilters, RacesPanel, RecordsGrid, WellnessStaff } from "../../components/performance";
import { ErrorState, Loading, PageHead } from "../../components/ui";
import { useAsync } from "../../lib/useAsync";
import { Link } from "../../router/router";
import { DashboardKpis, useEvolution } from "../athlete/AthleteDashboard";
import { t } from "../../i18n";

export function AthleteEvolutionPage({ orgId, athleteId }: { orgId: string; athleteId: string }) {
  const { can } = useAuth();
  const athlete = useAsync(() => api.get<AthleteDetail>(`/orgs/${orgId}/athletes/${athleteId}`), [orgId, athleteId]);
  const { filters, setFilters, bucket, setBucket, dash } = useEvolution(`/orgs/${orgId}/athletes/${athleteId}/performance`);
  const records = useAsync(() => api.get<Records>(`/orgs/${orgId}/athletes/${athleteId}/records`), [orgId, athleteId]);
  const d = dash.data;
  const strengthOnly = filters.sport === "strength";
  const base = `/org/${orgId}/atletas/${athleteId}`;
  if (athlete.error) return <ErrorState error={athlete.error} onRetry={athlete.reload} />;
  return (
    <>
      <p style={{ marginBottom: 8 }}>
        <Link to={`/org/${orgId}/atletas`}>{t("Atletas")}</Link> / <Link to={base}>{athlete.data?.full_name ?? t("Atleta")}</Link>
      </p>
      <PageHead
        title={t("Evolução: {full_name}", { full_name: athlete.data?.full_name ?? "" })}
        subtitle={t("Calculado das atividades importadas, da planilha publicada, das provas e dos check-ins consentidos.")}
        actions={
          <>
            <Link to={`${base}/planilha`} className="btn btn-secondary">
              {t("Planilha")}
            </Link>
            <Link to={`${base}/atividades`} className="btn btn-secondary">
              {t("Atividades")}
            </Link>
          </>
        }
      />
      {strengthOnly ? (
        <>
          <PeriodFilters value={filters} onChange={setFilters} bucket={bucket} onBucket={setBucket} />
          <StrengthOverviewSection path={`/orgs/${orgId}/athletes/${athleteId}/strength/overview`} weeks={filters.weeks} />
        </>
      ) : (
        <>
      {dash.error && <ErrorState error={dash.error} onRetry={dash.reload} />}
      {!d && dash.loading && <Loading />}
      {d && (
        <div className={dash.loading ? "refetching" : undefined}>
          <section aria-labelledby="alertas">
            <h2 id="alertas" style={{ marginBottom: 10 }}>
              {t("Alertas")}
            </h2>
            <AlertsList alerts={d.alerts ?? []} orgId={orgId} athleteId={athleteId} onChanged={dash.reload} />
          </section>
          <section className="section" aria-labelledby="indicadores">
            <h2 id="indicadores" style={{ marginBottom: 10 }}>
              {t("Indicadores")}
            </h2>
            <DashboardKpis d={d} />
          </section>
          <section className="section" aria-labelledby="graficos-evolucao">
            <h2 id="graficos-evolucao" style={{ marginBottom: 10 }}>
              {t("Gráficos de evolução")}
            </h2>
            <PeriodFilters value={filters} onChange={setFilters} bucket={bucket} onBucket={setBucket} />
            <EvolutionCharts dash={d} bucket={bucket} records={records.data} />
          </section>
          <GoalsPanel orgId={orgId} goals={d.goals} athleteId={athleteId} canManage={can("goals.manage")} onChanged={dash.reload} />
        </div>
      )}
      {filters.sport === "all" && <StrengthOverviewSection path={`/orgs/${orgId}/athletes/${athleteId}/strength/overview`} weeks={filters.weeks} />}
      {can("races.manage") && <RacesPanel orgId={orgId} athleteId={athleteId} self={false} />}
      <WellnessStaff orgId={orgId} athleteId={athleteId} />
      <BodyStaff orgId={orgId} athleteId={athleteId} />
      <section className="section" aria-labelledby="recordes">
        <h2 id="recordes" style={{ marginBottom: 10 }}>
          {t("Recordes pessoais")}
        </h2>
        {records.data && <RecordsGrid records={records.data.current} empty={t("Sem corridas importadas que cubram 1 km ou mais com séries.")} />}
      </section>
        </>
      )}
    </>
  );
}
